import React, { useEffect, useState } from "react";
import { Table, Button, DatePicker, Space, Skeleton, Input } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ExportOutlined, SearchOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { getReportAttendanceRequest } from "../slices/attendance";
import { useTypedSelector } from "../store";
import { useNavigate, Link } from "react-router-dom";

interface DataType {
  _id: string;
  status: string;
  tgl: string;
  eid: string;
  name: string;
  schedule: string;
  in: string;
  out: string;
  overtime: string;
  leave: string | null;
  approval_leave: string | null;
  approval_overtime: string | null;
  overtime_notes: string | null;
  leave_notes: string | null;
  location_in: string;
  location_out: string;
  coordinates_in: any[];
  coordinates_out: any[];
  work_hour: string;
  lates: string;
}

const columns: ColumnsType<DataType> = [
  {
    title: "Action",
    key: "action",
    render: (_, record) => (
      <Link
        to={`/attendance-detail/${record._id}`}
        style={{ color: "#1890ff" }}
      >
        Detail
      </Link>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: (a: any, b: any) => (a.status || "").localeCompare(b.status || ""),
    render: (text: any) => {
      if (text === "leave") return "Tidak Hadir";
      if (text === "absence") return "Hadir";
      if (text === "plan") return "Jadwal";
      if (text === "request") return "Permintaan Hadir";
      return text || "--";
    },
  },
  {
    title: "Tanggal",
    dataIndex: "tgl",
    key: "tgl",
    sorter: (a: any, b: any) =>
      new Date(a.tgl || "").getTime() - new Date(b.tgl || "").getTime(),
  },
  {
    title: "EID",
    dataIndex: "eid",
    key: "eid",
    sorter: (a: any, b: any) => (a.eid || "").localeCompare(b.eid || ""),
  },
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: (a: any, b: any) => (a.name || "").localeCompare(b.name || ""),
  },
  {
    title: "Schedule",
    dataIndex: "schedule",
    key: "schedule",
    sorter: (a: any, b: any) =>
      (a.schedule || "").localeCompare(b.schedule || ""),
    render: (text: any) => text || "--",
  },
  {
    title: "In",
    dataIndex: "in",
    key: "in",
    sorter: (a: any, b: any) => (a.in || "").localeCompare(b.in || ""),
  },
  {
    title: "Out",
    dataIndex: "out",
    key: "out",
    sorter: (a: any, b: any) => (a.out || "").localeCompare(b.out || ""),
  },
  {
    title: "Overtime",
    dataIndex: "overtime",
    key: "overtime",
    sorter: (a: any, b: any) => (a.overtime || 0) - (b.overtime || 0),
  },
  {
    title: "Leave",
    dataIndex: "leave",
    key: "leave",
    sorter: (a: any, b: any) => (a.leave || "").localeCompare(b.leave || ""),
    render: (text: any) => text || "--",
  },
  {
    title: "Approval Leave",
    dataIndex: "approval_leave",
    key: "approval_leave",
    sorter: (a: any, b: any) =>
      (a.approval_leave || "").localeCompare(b.approval_leave || ""),
    render: (text: any) => text || "--",
  },
  {
    title: "Approval Overtime",
    dataIndex: "approval_overtime",
    key: "approval_overtime",
    sorter: (a: any, b: any) =>
      (a.approval_overtime || "").localeCompare(b.approval_overtime || ""),
    render: (text: any) => text || "--",
  },
  {
    title: "Overtime Notes",
    dataIndex: "overtime_notes",
    key: "overtime_notes",
    sorter: (a: any, b: any) =>
      (a.overtime_notes || "").localeCompare(b.overtime_notes || ""),
    render: (text: any) => text || "--",
  },
  {
    title: "Leave Notes",
    dataIndex: "leave_notes",
    key: "leave_notes",
    sorter: (a: any, b: any) =>
      (a.leave_notes || "").localeCompare(b.leave_notes || ""),
    render: (text: any) => text || "--",
  },
  {
    title: "Location In",
    dataIndex: "location_in",
    key: "location_in",
    sorter: (a: any, b: any) =>
      (a.location_in || "").localeCompare(b.location_in || ""),
  },
  {
    title: "Location Out",
    dataIndex: "location_out",
    key: "location_out",
    sorter: (a: any, b: any) =>
      (a.location_out || "").localeCompare(b.location_out || ""),
  },
  {
    title: "Coordinates In",
    dataIndex: "coordinates_in",
    key: "coordinates_in",
    render: (text: any) => (text ? text.join(", ") : "--"),
  },
  {
    title: "Coordinates Out",
    dataIndex: "coordinates_out",
    key: "coordinates_out",
    render: (text: any) => (text ? text.join(", ") : "--"),
  },
  {
    title: "Work Hour",
    dataIndex: "work_hour",
    key: "work_hour",
    sorter: (a: any, b: any) => (a.work_hour || 0) - (b.work_hour || 0),
  },
  {
    title: "Lates",
    dataIndex: "lates",
    key: "lates",
    sorter: (a: any, b: any) => (a.lates || 0) - (b.lates || 0),
  },
];

const data: DataType[] = [];

const AttendanceDate: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { listAttendances, message, loading, authData, isLogin } =
    useTypedSelector((state) => {
      return {
        listAttendances: state.Attendance.reportList,
        message: state.Attendance.attendanceMessage,
        loading: state.Attendance.pageIsLoading,
        authData: state.Auth.loginData,
        isLogin: state.Auth.isLogin,
      };
    });
  const [startDate, setStartDate] = useState<any>(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState<any>(dayjs().endOf("month"));
  const [isLoading, setIsLoading] = useState<any>(false);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(listAttendances);
  console.log(startDate.format("YYYY-MM-DD"));
  console.log(endDate.format("YYYY-MM-DD"));

  useEffect(() => {
    if (!isLogin) {
      navigate("/login");
    } else {
      dispatch(
        getReportAttendanceRequest({
          stardate: startDate,
          enddate: endDate,
        }) as any
      );
    }
  }, [isLogin, dispatch, navigate, startDate, endDate]);

  const handleSearch = () => {
    setIsLoading(true);
    // Logika untuk pencarian berdasarkan tanggal
    console.log(
      "Searching from",
      startDate.format("YYYY-MM-DD"),
      "to",
      endDate.format("YYYY-MM-DD")
    );
    dispatch(
      getReportAttendanceRequest({
        stardate: startDate.format("YYYY-MM-DD"),
        enddate: endDate.format("YYYY-MM-DD"),
      }) as any
    ).then((res: any) => {
      // setIsLoading(false)
    });
  };

  const handleExportToExcel = () => {
    // Fungsi untuk eksport ke Excel
    console.log("Export to Excel");
    const worksheet = XLSX.utils.json_to_sheet(
      listAttendances.map((item) => ({
        status:
          item.status === "absence"
            ? "hadir"
            : item.status === "leave"
            ? "Tidak Hadir"
            : item.status,
        tgl: item.tgl,
        eid: item.eid,
        name: item.name,
        schedule: item.schedule || "--",
        in: item.in,
        out: item.out,
        overtime: item.overtime,
        leave: item.leave || "--",
        approval_leave: item.approval_leave || "--",
        approval_overtime: item.approval_overtime || "--",
        overtime_notes: item.overtime_notes || "--",
        leave_notes: item.leave_notes || "--",
        location_in: item.location_in,
        location_out: item.location_out,
        coordinates_in: item.coordinates_in || "--",
        coordinates_out: item.coordinates_out || "--",
        work_hour: item.work_hour,
        lates: item.lates,
      }))
    );

    // Buat workbook dan tambahkan worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

    // Ekspor workbook ke file Excel
    XLSX.writeFile(workbook, "AttendanceData.xlsx");
  };

  useEffect(() => {
    // Perbarui filteredData setiap kali employees berubah
    if (listAttendances.length > 0) {
      setFilteredData(listAttendances);
    }
  }, [listAttendances]);

  const handleSearchText = (value: string) => {
    setSearchText(value);
    const filtered = listAttendances.filter(
      (attendance) =>
        attendance.name.toLowerCase().includes(value.toLowerCase()) ||
        attendance.eid.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
  };

  return (
    <div>
      <Space
        style={{
          marginBottom: 16,
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Space>
          <Input
            placeholder="Search by name or EID"
            value={searchText}
            onChange={(e) => handleSearchText(e.target.value)}
            prefix={<SearchOutlined />}
            style={{ maxWidth: 300 }}
          />
          <DatePicker
            value={startDate}
            onChange={(date) => setStartDate(date ? date : dayjs())}
            placeholder="Start Date"
          />
          <DatePicker
            value={endDate}
            onChange={(date) => setEndDate(date ? date : dayjs())}
            placeholder="End Date"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          />
        </Space>
        <Button
          style={{
            backgroundColor: "#00A65A",
            borderColor: "#00A65A",
            color: "#fff",
          }}
          icon={<ExportOutlined />}
          onClick={handleExportToExcel}
        >
          Export to Excel
        </Button>
      </Space>
      {!loading ? (
        <Table
          columns={columns}
          dataSource={filteredData ? filteredData : listAttendances}
          scroll={{ x: "max-content" }}
        />
      ) : (
        <Skeleton active />
      )}
    </div>
  );
};

export default AttendanceDate;
